import { Link } from "wouter";
import logoBlack from "@assets/TORK_Logo_Black_1779647609157.png";
import logoWhite from "@assets/TORK_Logo_White_1779647609157.png";

export default function TermsPage() {
  return (
    <div className="min-h-screen bg-background text-foreground" data-testid="page-terms">
      <div className="max-w-3xl mx-auto px-6 py-12">
        <Link href="/">
          <a className="inline-flex items-center gap-2 mb-8" data-testid="link-home">
            <img src={logoBlack} alt="Tork" className="h-10 block dark:hidden" />
            <img src={logoWhite} alt="Tork" className="h-10 hidden dark:block" />
          </a>
        </Link>

        <h1 className="text-3xl sm:text-4xl font-bold mb-1" data-testid="text-terms-title">
          Terms of Service, Liability Waiver &amp; Risk Acknowledgment
        </h1>
        <p className="text-sm text-muted-foreground mb-2">Tork – Digital Domination Maps Domination SaaS</p>
        <p className="text-xs text-muted-foreground mb-10">
          Effective May 28, 2026 · <a href="https://tork.digitaldomination.ai" className="hover:underline">tork.digitaldomination.ai</a>
        </p>

        <div className="space-y-8 leading-relaxed text-[15px]">
          <Section title="1. Acceptance of Terms">
            <p>
              By accessing, purchasing, subscribing to, using, or participating in any service, software,
              automation, consulting, training, or platform provided by Tork – Digital Domination
              ("Company," "we," "us," or "our"), you ("Client," "User," or "Customer") acknowledge that you
              have read, understood, and agreed to be legally bound by these Terms of Service ("Terms").
            </p>
            <p>If you do not agree to these Terms, you may not access or use the Service.</p>
            <p>These Terms constitute a legally binding agreement between you and Tork – Digital Domination.</p>
          </Section>

          <Section title="2. Description of Service">
            <p>
              Tork – Digital Domination provides software, automation systems, local SEO tools, digital
              marketing services, consulting, optimization strategies, lead generation systems, Google
              Business Profile management assistance, and related marketing technologies ("Service").
            </p>
            <p>The Service may include:</p>
            <BulletList items={[
              "Google Business Profile optimization",
              "Local SEO strategies",
              "Map ranking systems",
              "Automation tools",
              "Lead generation systems",
              "Reputation management",
              "Consulting",
              "Training",
              "AI tools",
              "Third-party integrations",
              "Marketing workflows",
            ]} />
            <p>The Company may modify, suspend, discontinue, or update any part of the Service at any time without notice.</p>
          </Section>

          <Section title="3. No Guarantees or Warranties">
            <p className="uppercase font-semibold text-foreground">
              Client expressly acknowledges and agrees that Tork – Digital Domination makes absolutely no guarantees of any kind.
            </p>
            <p>This includes, without limitation, no guarantees regarding:</p>
            <BulletList items={[
              "Rankings", "Map pack placement", "Lead generation", "Call volume", "Revenue",
              "ROI", "Business growth", "Profile visibility", "Search performance",
              "Verification approval", "Reverification avoidance", "Suspension prevention",
              "Reinstatement success", "Review retention", "Account longevity",
              "Traffic increases", "Conversion rates", "SEO outcomes",
              "Business performance", "Platform stability",
            ]} />
            <p>All services are provided on an "AS IS" and "AS AVAILABLE" basis.</p>
            <p>The Company disclaims all express or implied warranties, including but not limited to:</p>
            <BulletList items={["Merchantability", "Fitness for a particular purpose", "Non-infringement", "Availability", "Accuracy", "Reliability"]} />
            <p>
              Past performance, case studies, testimonials, screenshots, examples, or projected results do
              not guarantee future outcomes.
            </p>
          </Section>

          <Section title="4. Platform Risk Acknowledgment">
            <p>
              Client understands and voluntarily accepts that use of Google Business Profiles, Google Maps,
              local SEO strategies, automation systems, and third-party digital marketing platforms carries
              inherent risk.
            </p>
            <p>Client expressly acknowledges the possibility of:</p>
            <BulletList items={[
              "Soft suspensions", "Hard suspensions", "Listing removals", "Verification failures",
              "Video verification requests", "Postcard verification failures", "Reverification requests",
              "Account restrictions", "Ranking fluctuations", "Deindexing", "Lead loss",
              "Review removals", "Business profile disabling", "Spam flags",
              "Algorithmic penalties", "Temporary or permanent visibility loss",
              "Third-party enforcement actions",
            ]} />
            <p>Client further acknowledges that Google and other platforms may change:</p>
            <BulletList items={[
              "Policies", "Verification methods", "Spam filters", "Enforcement standards",
              "Ranking algorithms", "Visibility systems", "Review systems", "Business rules",
            ]} />
            <p>at any time without warning.</p>
            <p>Tork – Digital Domination shall not be responsible or liable for any such outcomes.</p>
          </Section>

          <Section title="5. No Guarantee of Reinstatement or Verification">
            <p>Tork – Digital Domination does not guarantee:</p>
            <BulletList items={[
              "Reinstatement of suspended profiles",
              "Verification approval",
              "Reverification success",
              "Restoration of removed listings",
              "Recovery of disabled accounts",
              "Prevention of future suspensions",
            ]} />
            <p>
              Client acknowledges that all platform decisions are controlled solely by the applicable
              third-party platform providers, including Google.
            </p>
          </Section>

          <Section title="6. Client Responsibilities">
            <p>Client is solely responsible for:</p>
            <BulletList items={[
              "Compliance with all applicable laws",
              "Compliance with Google policies",
              "Accuracy of business information",
              "Ownership of listings and accounts",
              "Maintaining account access",
              "Maintaining login credentials",
              "Providing truthful information",
              "Providing requested documentation",
              "Responding to verification requests",
              "Monitoring account notifications",
              "Business operations and conduct",
            ]} />
            <p>
              Client acknowledges that failure to follow Company recommendations or platform policies may
              negatively affect results.
            </p>
          </Section>

          <Section title="7. Assumption of Risk">
            <p>By using the Service, Client knowingly and voluntarily assumes all risks associated with:</p>
            <BulletList items={[
              "Google Business Profile optimization",
              "Local SEO activities",
              "Digital marketing",
              "Automation systems",
              "Third-party platform integrations",
              "Ranking volatility",
              "Platform enforcement actions",
            ]} />
            <p>Client agrees that they are solely responsible for any resulting business impact.</p>
          </Section>

          <Section title="8. Limitation of Liability">
            <p className="uppercase font-semibold text-foreground">
              To the maximum extent permitted by law, Tork – Digital Domination, its owners, employees,
              contractors, affiliates, partners, licensors, and agents shall not be liable for:
            </p>
            <BulletList items={[
              "Lost profits", "Lost revenue", "Lost business opportunities", "Lost leads",
              "Loss of rankings", "Suspension damages", "Reverification issues", "Data loss",
              "Reputation damage", "Account disabling", "Platform penalties",
              "Business interruption", "Indirect damages", "Incidental damages",
              "Consequential damages", "Special damages", "Punitive damages",
            ]} />
            <p className="uppercase font-semibold text-foreground">
              Under no circumstances shall total liability exceed the total amount paid by Client to
              Tork – Digital Domination during the thirty (30) days preceding the claim.
            </p>
          </Section>

          <Section title="9. Indemnification">
            <p>
              Client agrees to indemnify, defend, and hold harmless Tork – Digital Domination and its
              affiliates from any claims, liabilities, damages, losses, costs, expenses, or legal fees
              arising from:
            </p>
            <BulletList items={[
              "Client misuse of the Service",
              "Violation of platform policies",
              "Business disputes",
              "Listing ownership disputes",
              "Regulatory violations",
              "Misrepresentation",
              "Fraudulent business activity",
              "Third-party claims",
              "User-generated content",
            ]} />
          </Section>

          <Section title="10. No Affiliation with Google">
            <p>
              Tork – Digital Domination is not affiliated with, endorsed by, sponsored by, or officially
              connected with Google LLC.
            </p>
            <p>Google, Google Maps, and Google Business Profile are trademarks of Google LLC.</p>
          </Section>

          <Section title="11. Payment Terms">
            <p>All payments are final and non-refundable unless otherwise stated in writing.</p>
            <p>Client agrees that:</p>
            <BulletList items={[
              "Subscription fees may recur automatically",
              "Failure to pay may result in account suspension",
              "Services may be terminated for non-payment",
            ]} />
            <p>Client is responsible for maintaining valid payment information.</p>
          </Section>

          <Section title="12. No Refunds for Platform Actions">
            <p>
              Client expressly agrees that the following shall NOT constitute grounds for refunds,
              disputes, or chargebacks:
            </p>
            <BulletList items={[
              "Suspensions", "Reverification requests", "Ranking fluctuations", "Lead quality",
              "Lead volume", "Verification failures", "Loss of visibility", "Google policy changes",
              "Algorithm updates", "Business performance issues",
            ]} />
          </Section>

          <Section title="13. Chargeback &amp; Payment Dispute Policy">
            <p>
              Client agrees not to initiate chargebacks, payment disputes, or payment reversals for
              services rendered.
            </p>
            <p>Any billing dispute must first be submitted in writing to the Company for resolution.</p>
            <p>
              Chargebacks made in violation of these Terms may be considered a material breach of contract
              and may be submitted as evidence in arbitration or legal proceedings.
            </p>
            <p>
              Client agrees to reimburse the Company for any chargeback fees, collection costs, or legal
              expenses incurred.
            </p>
          </Section>

          <Section title="14. Intellectual Property">
            <p>
              All software, systems, workflows, automation processes, templates, strategies, training
              materials, branding, and proprietary content remain the exclusive property of
              Tork – Digital Domination.
            </p>
            <p>Client may not:</p>
            <BulletList items={["Copy", "Resell", "Redistribute", "Reverse engineer", "Reproduce", "Republish", "Modify", "License"]} />
            <p>any Company intellectual property without written permission.</p>
          </Section>

          <Section title="15. Service Availability">
            <p>
              The Company does not guarantee uninterrupted service, uptime, availability, compatibility,
              or error-free operation.
            </p>
            <p>
              Temporary outages, maintenance, software failures, API issues, or third-party interruptions
              may occur at any time.
            </p>
          </Section>

          <Section title="16. Beta Features">
            <p>Certain features may be identified as beta, experimental, or testing features.</p>
            <p>Such features are provided without warranties of any kind and may be modified or discontinued at any time.</p>
          </Section>

          <Section title="17. Termination">
            <p>
              Tork – Digital Domination reserves the right to suspend, restrict, or terminate access to
              the Service at any time, with or without notice, for:
            </p>
            <BulletList items={[
              "Violation of these Terms", "Fraudulent activity", "Abuse", "Harassment",
              "Non-payment", "Misuse of the platform", "Conduct deemed harmful to the Company",
            ]} />
            <p>No refunds shall be issued upon termination.</p>
          </Section>

          <Section title="18. Compliance with Laws">
            <p>Client agrees not to use the Service for:</p>
            <BulletList items={[
              "Illegal activity", "Fraudulent activity", "Misleading business practices",
              "Spam", "Deceptive marketing", "Policy violations",
            ]} />
            <p>Client is solely responsible for compliance with all laws and regulations.</p>
          </Section>

          <Section title="19. Dispute Resolution &amp; Binding Arbitration">
            <p>
              Any dispute, claim, or controversy arising out of or relating to these Terms or the Service
              shall be resolved exclusively through final and binding arbitration.
            </p>
            <p>Client waives:</p>
            <BulletList items={[
              "The right to a jury trial",
              "The right to participate in class actions",
              "The right to consolidated proceedings",
            ]} />
            <p>Arbitration shall be conducted in the jurisdiction selected by Tork – Digital Domination.</p>
          </Section>

          <Section title="20. Limitation Period">
            <p>
              Any claim arising from the Service must be brought within six (6) months of the event giving
              rise to the claim.
            </p>
            <p>Claims brought after this period shall be permanently barred.</p>
          </Section>

          <Section title="21. Electronic Consent">
            <p>
              By checking the acceptance box, electronically signing, purchasing, subscribing, or using
              the Service, Client acknowledges and agrees that:
            </p>
            <BulletList items={[
              "They have read and understood these Terms",
              "They voluntarily assume all risks",
              "They understand there are no guarantees",
              "They accept full responsibility for platform-related outcomes",
              "They release Tork – Digital Domination from liability relating to suspensions, reverifications, rankings, visibility, or platform enforcement actions",
            ]} />
            <p>Electronic acceptance shall have the same legal effect as a physical signature.</p>
          </Section>

          <Section title="22. Severability">
            <p>
              If any provision of these Terms is found unenforceable, the remaining provisions shall
              remain in full force and effect.
            </p>
          </Section>

          <Section title="23. Modifications to Terms">
            <p>Tork – Digital Domination reserves the right to modify these Terms at any time.</p>
            <p>Continued use of the Service constitutes acceptance of updated Terms.</p>
          </Section>

          <Section title="24. Contact Information">
            <p>
              Tork – Digital Domination<br />
              Website: <a href="https://tork.digitaldomination.ai" className="text-primary hover:underline">tork.digitaldomination.ai</a>
            </p>
            <p>Support inquiries must be submitted through official Company channels.</p>
          </Section>
        </div>

        <div className="mt-16 pt-6 border-t border-border text-sm text-muted-foreground">
          <Link href="/privacy"><a className="hover:underline" data-testid="link-privacy">Privacy Policy</a></Link>
          {" · "}
          <Link href="/"><a className="hover:underline" data-testid="link-home-bottom">Home</a></Link>
        </div>
      </div>
    </div>
  );
}

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <section>
      <h2 className="text-xl font-semibold mb-3 text-foreground">{title}</h2>
      <div className="space-y-3 text-muted-foreground">{children}</div>
    </section>
  );
}

function BulletList({ items }: { items: string[] }) {
  return (
    <ul className="list-disc pl-6 space-y-1">
      {items.map((item) => (
        <li key={item}>{item}</li>
      ))}
    </ul>
  );
}
